import React, { useContext } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
	ITotalPrice,
	ITypeVignettenCheckoutFields,
	OfferContext,
	VignetteContext,
	getLabelValue,
	getServiceFeePerProduct,
	getTotalServiceFee,
} from '@adac-mp/shared';
import { Accordion, DiscountLabel, Typography, TypographySize } from '../../base-components/index';
import { useFlowId } from '../../hooks/useFlowId';

interface IPriceInformationProps {
	className?: string;
	hasMembership?: boolean;
	expandable?: boolean;
	hasMembershipBenefit?: boolean;
	labelSize?: TypographySize;
	simple?: boolean;
	showDiscount?: boolean;
	totalPrice: ITotalPrice;
}

export const PriceInformation = ({
	className,
	hasMembership = false,
	hasMembershipBenefit = false,
	simple = false,
	showDiscount = false,
	totalPrice,
	labelSize = 'xxlEven',
	expandable,
}: IPriceInformationProps) => {
	const flowId = useFlowId();
	const isSlovenianFlow = flowId === 7;
	const { labels } = useContext(OfferContext);
	const { cartItems, cartInfo, contentfulLabels } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignettenCheckoutFields;
	const { decimalAmount, label, amount, additionalInfo } = totalPrice;
	const totalDiscount = Number(cartInfo?.sumDiscount?.decimalAmount);
	const articlePrice = `${cartInfo?.sumArticlelPrice?.amount}€`;
	const serviceFeeDiscount = `${cartInfo?.sumDiscount?.amount}€`;
	const hasFlexProduct = cartInfo?.flexProduct;

	const totalServiceFee = getTotalServiceFee(cartInfo?.sumDiscount, cartInfo?.sumServiceFee, hasMembership);
	const serviceFeePerProduct = getServiceFeePerProduct(
		cartInfo?.sumDiscount,
		cartInfo?.sumServiceFee,
		cartItems.length,
		hasMembership,
	);

	const renderTexts = () => (
		<div className="flex flex-col gap-1.5">
			<Typography component="p" variant="title" color="title" size={labelSize}>
				{label}
			</Typography>
			<div className="flex flex-col">
				<Typography component="p" color="text">
					{`${decimalAmount !== 0 ? getLabelValue('mp.vat', labels) || 'inkl. MwSt.' : ''}${
						decimalAmount !== 0 && hasFlexProduct ? ', ' : ''
					}
					${
						hasFlexProduct
							? currentLabels?.cart?.additionalFlexFees ||
							  'zzgl. Mautgebühren bei Nutzung der Streckenmautabschnitte'
							: ''
					}`}
				</Typography>
				{additionalInfo && <Typography color="text">{additionalInfo}</Typography>}
			</div>
		</div>
	);

	const renderDiscountMessage = () => {
		let message = '';
		if (hasMembership && hasMembershipBenefit)
			message = showDiscount
				? currentLabels?.priceDetails?.memberPrice || 'Preis für Mitglieder'
				: `${getLabelValue('mp.incl', labels) || 'inkl.'} ${serviceFeeDiscount} ${
						currentLabels?.priceDetails?.serviceDiscount || 'Servicerabatt'
				  }`;
		return message;
	};

	const getTotalPriceWithoutDiscount = (): string => {
		return String(Math.abs(Number(decimalAmount) + totalDiscount).toFixed(2)).replace('.', ',');
	};

	const renderTotalPrice = () => (
		<div className="flex flex-col gap-1.5 items-end">
			{amount ? (
				<div className="flex items-baseline">
					{showDiscount && hasMembership && hasMembershipBenefit ? (
						<>
							<Typography size="baseLg" className="line-through pr-1.5">
								{getTotalPriceWithoutDiscount()}
								{'€ '}
							</Typography>
							<Typography color="title" variant="title" size="xl">
								{amount}
							</Typography>
						</>
					) : (
						<Typography color="title" variant="title" size="xl">
							{amount}
						</Typography>
					)}
				</div>
			) : (
				<Skeleton inline height="100%" width={100} baseColor="#ebebeb" highlightColor="#f4f4f4" />
			)}
			{hasMembership && hasMembershipBenefit ? <DiscountLabel label={renderDiscountMessage()} /> : null}
		</div>
	);

	const renderPricings = () => {
		const pricings: { label: React.ReactNode; value: string }[] = [];
		if (hasMembershipBenefit)
			pricings.push(
				{ label: getLabelValue('mp.article', labels) || 'Artikel', value: articlePrice },
				{
					label: (
						<>
							{currentLabels?.priceDetails?.serviceFee || 'Servicegebühr'} <br />{' '}
							{`(${serviceFeePerProduct} / ${getLabelValue('mp.article', labels) || 'Artikel'})`}
						</>
					),
					value: totalServiceFee,
				},
			);
		if (hasMembership && hasMembershipBenefit)
			pricings.push({
				label: currentLabels?.priceDetails?.serviceDiscount || 'Servicerabatt',
				value: `-${serviceFeeDiscount}`,
			});
		if (hasFlexProduct) {
			if (cartInfo?.sumArticlelPrice?.decimalAmount && cartInfo?.sumArticlelPrice?.decimalAmount > 0) {
				pricings.push({ label: getLabelValue('mp.article', labels) || 'Artikel', value: articlePrice });
			}
			pricings.push({
				label: currentLabels?.priceDetails?.flexServiceHeadline || 'FLEX-Service',
				value: currentLabels?.priceDetails?.payLater || 'Automatische Abbuchung nach Durchfahrt',
			});
		}
		return pricings.map(({ label: currLabel, value }, index) => (
			<Typography
				key={index}
				color="title"
				size="baseLg"
				className="flex justify-between items-baseline gap-3 w-full">
				{currLabel}
				<Typography color="title" size="baseLg">
					{value}
				</Typography>
			</Typography>
		));
	};

	return (
		<>
			{!simple && (isSlovenianFlow || expandable) && (
				<Accordion
					title={currentLabels?.priceDetails?.priceDetailsHeadline || 'Preisdetails'}
					titleSize="lgEven"
					color="gray-600"
					chevronColor="#3D3D3D"
					className="pt-3.5 px-1.5 pb-3 rounded-b-none relative after:content-[''] after:w-[calc(100%_-_48px)] after:h-px after:bg-gray-500 after:absolute after:bottom-0 after:left-4 after:translate-y-full">
					<div className="px-3 pt-3 flex flex-col gap-3">{renderPricings()}</div>
				</Accordion>
			)}
			<div className={`flex justify-between gap-1.5 ${className}`}>
				{renderTexts()}
				{renderTotalPrice()}
			</div>
		</>
	);
};
